import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  LogOut, User, Menu, X, ChevronDown,
  Mic, BarChart2, HeadphonesIcon, MessageSquare, Workflow, ShieldCheck,
  Package, KeyRound, CreditCard, UserPlus,
  Download, Users, Calendar, Monitor
} from 'lucide-react';

const products = [
  {
    path: '/ai-voice-agent',
    label: 'AI Voice Agent',
    description: 'Intelligent voice automation for enterprise',
    icon: Mic,
  },
  {
    path: '/ai-customer-care',
    label: 'AI Customer Care',
    description: 'Automated support at scale',
    icon: HeadphonesIcon,
  },
  {
    path: '/analytics-chat-agents',
    label: 'Analytics & Chat Agents',
    description: 'Conversational AI for data insights',
    icon: BarChart2,
  },
  {
    path: '/ai-web-chat',
    label: 'AI Web Chat Integration',
    description: 'Branded chat for customer & employee support',
    icon: MessageSquare,
  },
  {
    path: '/ai-workflow-automation',
    label: 'AI Workflow Automation',
    description: 'End-to-end execution engine for IT & customer care',
    icon: Workflow,
  },
  {
    path: '/ai-compliance-studio',
    label: 'AI Compliance Studio',
    description: 'Governance, audit trails & QA for AI support',
    icon: ShieldCheck,
  },
];

const usecases = [
  {
    group: 'Customer Care',
    items: [
      { path: '/use-cases/order-status', label: 'Order Status & Returns', description: 'WISMO, delivery updates, returns automation', icon: Package },
      { path: '/use-cases/billing-payments', label: 'Billing & Payments', description: 'Invoices, refunds, subscription changes', icon: CreditCard },
      { path: '/use-cases/identity-verification', label: 'Identity Verification', description: 'Secure self-service account updates', icon: ShieldCheck },
      { path: '/use-cases/case-triage', label: 'Case Triage & Agent Assist', description: 'Intelligent routing and agent suggestions', icon: BarChart2 },
      { path: '/use-cases/appointment-scheduling', label: 'Appointment Scheduling', description: 'Book, reschedule, and send reminders', icon: Calendar },
    ],
  },
  {
    group: 'IT Helpdesk',
    items: [
      { path: '/use-cases/password-reset', label: 'Password Reset & Unlock', description: 'MFA-verified resets via Azure AD / Okta', icon: KeyRound },
      { path: '/use-cases/employee-onboarding', label: 'Employee Onboarding', description: 'HR-triggered provisioning end-to-end', icon: UserPlus },
      { path: '/use-cases/software-provisioning', label: 'Software Provisioning', description: 'Catalog + approvals + Intune/JAMF deploy', icon: Download },
      { path: '/use-cases/groups-management', label: 'DL & Groups Management', description: 'Exchange + Azure AD group automation', icon: Users },
      { path: '/use-cases/hardware-support', label: 'Hardware Support', description: 'Troubleshoot, diagnose, dispatch, replace', icon: Monitor },
    ],
  },
];

const DropdownMenu = ({ items, isOpen }) => {
  if (!isOpen) return null;
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50">
      <div className="p-2">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-start gap-3 px-4 py-3 rounded-xl hover:bg-indigo-50 transition-colors group"
            >
              <div className="mt-0.5 w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-200 transition-colors">
                <Icon className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800 group-hover:text-indigo-700">{item.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

const GroupedDropdownMenu = ({ groups, isOpen }) => {
  if (!isOpen) return null;
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[640px] bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50">
      <div className="p-4 grid grid-cols-2 gap-4">
        {groups.map((group) => (
          <div key={group.group}>
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600 px-2 mb-2">{group.group}</p>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-start gap-3 px-2 py-2 rounded-xl hover:bg-indigo-50 transition-colors group"
                  >
                    <div className="mt-0.5 w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-200 transition-colors">
                      <Icon className="w-3.5 h-3.5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800 group-hover:text-indigo-700">{item.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Navigation = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [openDropdown, setOpenDropdown] = React.useState(null); // 'products' | 'usecases' | null
  const [mobileExpanded, setMobileExpanded] = React.useState(null);
  const navRef = React.useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (items) => items.some((i) => location.pathname === i.path);
  const isUsecaseActive = () => usecases.some((g) => g.items.some((i) => location.pathname === i.path));

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  React.useEffect(() => {
    setOpenDropdown(null);
    setMobileMenuOpen(false);
    setMobileExpanded(null);
  }, [location.pathname]);

  const toggleDropdown = (name) => {
    setOpenDropdown((prev) => (prev === name ? null : name));
  };

  return (
    <nav ref={navRef} className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 flex-shrink-0" data-testid="nav-logo-link">
            <div className="w-10 h-10 rounded-md overflow-hidden bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
              <img
                src={require('../assets/digian-logo.png')}
                alt="Digian.ai logo"
                className="w-8 h-8 object-cover"
              />
            </div>
            <span className="font-['Outfit'] font-bold text-xl text-slate-900">digian.ai</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {/* Home */}
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:text-indigo-600 hover:bg-indigo-50 ${
                isActive('/') ? 'text-indigo-600 bg-indigo-50' : 'text-slate-600'
              }`}
            >
              Home
            </Link>

            {/* Products dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('products')}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:text-indigo-600 hover:bg-indigo-50 ${
                  isGroupActive(products) || openDropdown === 'products'
                    ? 'text-indigo-600 bg-indigo-50'
                    : 'text-slate-600'
                }`}
              >
                Products
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${openDropdown === 'products' ? 'rotate-180' : ''}`}
                />
              </button>
              <DropdownMenu items={products} isOpen={openDropdown === 'products'} />
            </div>

            {/* Use Cases dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('usecases')}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:text-indigo-600 hover:bg-indigo-50 ${
                  isUsecaseActive() || openDropdown === 'usecases'
                    ? 'text-indigo-600 bg-indigo-50'
                    : 'text-slate-600'
                }`}
              >
                Use Cases
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${openDropdown === 'usecases' ? 'rotate-180' : ''}`}
                />
              </button>
              <GroupedDropdownMenu groups={usecases} isOpen={openDropdown === 'usecases'} />
            </div>

            {/* Blogs */}
            <Link
              to="/blog"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:text-indigo-600 hover:bg-indigo-50 ${
                isActive('/blog') ? 'text-indigo-600 bg-indigo-50' : 'text-slate-600'
              }`}
            >
              Blogs
            </Link>

            {/* Contact */}
            <Link
              to="/contact"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:text-indigo-600 hover:bg-indigo-50 ${
                isActive('/contact') ? 'text-indigo-600 bg-indigo-50' : 'text-slate-600'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Desktop Auth / CTA */}
          <div className="hidden md:flex items-center space-x-3">
            <Link to="/voice-experience" data-testid="nav-voice-experience-btn">
              <Button variant="outline" size="sm" className="text-indigo-600 border-indigo-200 hover:bg-indigo-50">
                <Mic className="w-3.5 h-3.5 mr-1.5" />
                Try Voice
              </Button>
            </Link>
            {isAuthenticated ? (
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-slate-100">
                  <User className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">{user?.full_name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn">
                  <Button variant="ghost" size="sm">Login</Button>
                </Link>
                <Link to="/contact" data-testid="nav-contact-sales-btn">
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    Contact Sales
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-700" />
            ) : (
              <Menu className="w-6 h-6 text-slate-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">

            <Link
              to="/"
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/') ? 'text-indigo-600 bg-indigo-50' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              Home
            </Link>

            {/* Mobile Products */}
            <div>
              <button
                onClick={() => setMobileExpanded((p) => (p === 'products' ? null : 'products'))}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
              >
                Products
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${mobileExpanded === 'products' ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileExpanded === 'products' && (
                <div className="mt-1 ml-4 space-y-1">
                  {products.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                      >
                        <Icon className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Mobile Use Cases */}
            <div>
              <button
                onClick={() => setMobileExpanded((p) => (p === 'usecases' ? null : 'usecases'))}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
              >
                Use Cases
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${mobileExpanded === 'usecases' ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileExpanded === 'usecases' && (
                <div className="mt-1 ml-4 space-y-2">
                  {usecases.map((group) => (
                    <div key={group.group}>
                      <p className="px-4 pt-2 pb-1 text-xs font-bold uppercase tracking-widest text-indigo-600">{group.group}</p>
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                          >
                            <Icon className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/blog"
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/blog') ? 'text-indigo-600 bg-indigo-50' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              Blogs
            </Link>

            <Link
              to="/contact"
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/contact') ? 'text-indigo-600 bg-indigo-50' : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              Contact
            </Link>

            <div className="pt-3 border-t border-slate-100 space-y-2">
              <Link to="/voice-experience">
                <Button variant="outline" size="sm" className="w-full text-indigo-600 border-indigo-200">
                  <Mic className="w-3.5 h-3.5 mr-1.5" />
                  Try Voice Experience
                </Button>
              </Link>
              {isAuthenticated ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="w-full justify-start"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" size="sm" className="w-full">Login</Button>
                  </Link>
                  <Link to="/contact">
                    <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
                      Contact Sales
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
